import { useCallback, useState, type ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { type AlertColor } from '@mui/material/Alert';
import { ToastContext } from './toastContext';

/**
 * App-wide transient feedback. Lives above the routes so a toast triggered just
 * before navigation (e.g. saving an entry) still shows on the next screen.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState<ReactNode>('');
    const [severity, setSeverity] = useState<AlertColor>('success');

    const notify = useCallback(
        (next: ReactNode, nextSeverity: AlertColor = 'success') => {
            setMessage(next);
            setSeverity(nextSeverity);
            setOpen(true);
        },
        []
    );

    return (
        <ToastContext.Provider value={{ notify }}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={3000}
                onClose={(_event, reason) => {
                    if (reason === 'clickaway') {
                        return;
                    }
                    setOpen(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setOpen(false)}
                    severity={severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
}
